import {YandexDeliverySummary} from "../helpers/yandexDeliveryDetails";
import {YandexDeliveryControls} from "../modal/yandexDelivery";
import React, {useEffect, useMemo, useState} from "react";
// Type-only: teaches bindActionCreators that thunks return their resolved value rather than the
// thunk itself. Must stay `import type`, as the package ships this as a .d.ts with no runtime
// module - a bare `import "redux-thunk/extend-redux"` typechecks but fails webpack resolution.
import type {} from "redux-thunk/extend-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import {Button, Tooltip} from "antd";
import {CopyOutlined} from "@ant-design/icons";
import {OrderPaymentStatus} from "./orderPaymentStatus";
import {IOrder} from "../../models/orders/order";
import {CURRENCY} from "../../utils/options/options";
import {useGlobalContext, useUserContext} from "../../hooks/globalContext";
import {useActions} from "migration/shared/lib/hooks/useActions";
import {useTypedSelector} from "migration/shared/lib/hooks/useTypedSelector";
import {isEmpty} from "lodash";
import {addNotification, addNotificationWithButton} from "../../utils/toast/toast";

interface IIikoOrderItem {
    order: IOrder
    iikoStatus: 'IN_PROGRESS' | 'ERROR' | 'DONE' | "Closed" | "Cancelled" | "Deleted" | null,
    iikoDetailedStatus: string | null,
    iikoOrderTask: {
        tableOrderIikoId: string | null,
        deliveryOrderIikoId: string | null
    } | null,
    setIikoOrderModalInfo: React.Dispatch<React.SetStateAction<{ order: IOrder | null, active: boolean }>>
    createdAt: string | null
    trigger: boolean
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>
    setOrders: React.Dispatch<React.SetStateAction<IOrder[] | null>>
}


export function IikoOrderItem(props: IIikoOrderItem) {
    const navigate = useNavigate();
    const {i18n, api} = useGlobalContext()
    const {user, menu, currentMenuId} = useUserContext()

    const [archiveLoading, setArchiveLoading] = useState(false);
    const [loaders, setLoaders] = useState({
        approveIikoOrder: false,
        recoverIikoOrder: false,
        cancelIikoDeliveryOrder: false
    });
    const [stillCanceling, setStillCanceling] = useState(false);
    const {iikoMenuSettings} = useTypedSelector(state => state.iiko)
    const {approveIikoOrder, probeIikoOrderRecovery, executeIikoOrderRecovery, cancelIikoDeliveryOrder} = useActions();

    const handleApproveIikoOrder = async () => {
        setLoaders({...loaders, approveIikoOrder: true});
        await approveIikoOrder({
            menuId: currentMenuId,
            orderId: props.order?.id,
            organizationId: user?.org?.id || ""
        }, {navigate});
        setLoaders({...loaders, approveIikoOrder: false});
        props.setTrigger(!props.trigger);
    }

    const setRecoverLoader = (value: boolean) => setLoaders((prev) => ({...prev, recoverIikoOrder: value}));

    const handleRecoverIikoOrder = async () => {
        setRecoverLoader(true);
        const probe = await probeIikoOrderRecovery({
            menuId: currentMenuId,
            orderId: props.order?.id,
            organizationId: user?.org?.id || ""
        }, {navigate});
        if (!probe) {
            setRecoverLoader(false);
            return;
        }
        if (probe.action === "none") {
            setRecoverLoader(false);
            addNotification({message: probe.message, type: "warning"});
            return;
        }
        // The confirm dialog outlives this call, so the loader stays held until it is answered or
        // dismissed. Releasing it here would leave the button live for the dialog's whole lifetime
        // and let a second press open a second dialog.
        const action = probe.action;
        let submitted = false;
        addNotificationWithButton({
            message: probe.message,
            danger: action === "reissue",
            onSubmit: async () => {
                // The dialog's own buttons are rendered once with a frozen loading={false}, and the
                // notification stays clickable through its 600ms exit animation, so the confirm
                // button can be pressed again while the first execute is still in flight. On the
                // re-issue path that would be a second mint attempt.
                if (submitted) return;
                submitted = true;
                // Confirming also dismisses the toast, so onDismiss may already have released the
                // loader by the time we get here. Re-raise it rather than depend on the ordering.
                setRecoverLoader(true);
                const outcome = await executeIikoOrderRecovery({
                    menuId: currentMenuId,
                    orderId: props.order?.id,
                    organizationId: user?.org?.id || "",
                    action
                }, {navigate});
                setRecoverLoader(false);
                if (!outcome) return;
                // The server can reject a re-issue at its guards and quietly fall back to a requeue,
                // so the operator is told what it executed and never what we asked for.
                if (outcome.executedAction === "reissue") {
                    addNotification({message: i18n.iiko_order_reissued(), type: "success"});
                } else if (outcome.executedAction === "requeue") {
                    addNotification({message: i18n.iiko_order_requeued(), type: "success"});
                } else {
                    addNotification({message: i18n.iiko_recovery_outcome_unknown(), type: "warning"});
                }
                props.setTrigger((trigger) => !trigger);
            },
            onDismiss: () => {
                if (submitted) return;
                setRecoverLoader(false);
            },
            loading: false,
            i18n
        });
    }

    function onArchiveOrder(id: string) {
        api.archiveOrder({setLoading: setArchiveLoading}, currentMenuId, id).then((res) => {
            if (res.success) {
                props.setOrders((orders) => orders ? orders.filter((o) => o.id !== id) : null)
            }
        })
    }

    const handleCancelIikoDeliveryOrder = async () => {
        setLoaders({...loaders, cancelIikoDeliveryOrder: true});
        await cancelIikoDeliveryOrder({
            menuId: currentMenuId,
            orderId: props.order?.id,
            organizationId: user?.org?.id || ""
        }, {navigate});
        setLoaders({...loaders, cancelIikoDeliveryOrder: false});
        props.setTrigger(!props.trigger);
    }

    useEffect(() => {
        if (!props.order?.id) return;
        const cancelExpiresAt = JSON.parse(localStorage.getItem(`${currentMenuId}-${props.order?.id}`) || "{}")?.expiresAt;
        if (cancelExpiresAt) {
            if (moment(cancelExpiresAt).isAfter(moment(), "minute")) {
                setStillCanceling(true);
            } else {
                localStorage.removeItem(`${currentMenuId}-${props.order?.id}`);
            }
        }
    }, [props.order, currentMenuId]);

    return (
        <tr key={props.order.iiko?.order.externalNumber}>
            <td data-title="№">
                <span style={{display: "flex", alignItems: "center", gap: "8px"}}>
                    <a
                        href="#/"
                        onClick={(event) => {
                            event.preventDefault();
                            props.setIikoOrderModalInfo({order: props.order, active: true});
                        }}
                    >
                        {props.order.iiko?.order.externalNumber}
                    </a>
                    {props.order.id && user?.isStaff && (
                        <Tooltip title={`ID: ${props.order.id}`}>
                            <CopyOutlined
                                style={{cursor: "pointer", fontSize: "14px"}}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                        navigator.clipboard.writeText(props.order.id);
                                    }
                                }}
                            />
                        </Tooltip>
                    )}
                </span>
            </td>
            <td data-title={i18n.customer()}>
                <span className="text-secondary">{props.order.iiko?.order.customer.Name}</span><br/>
                <span className="">{props.order.iiko?.order.phone}</span>
            </td>
            <td data-title={i18n.order_content()}
                className="lg-text-right">
                {
                    props.order.iiko?.order.items?.map((item, index) => {
                        return (
                            <div key={`${item.productId}-${index}-${props.order.iiko?.order.externalNumber}`}>
                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                    <div>
                                        <span>{(item.name || {})[menu.i18n.defaultLang] || ""} {item.productSizeName} </span>
                                        {item.modifiers ?
                                            <span className="text-secondary">
                                                {item.modifiers.map((modifier) => {
                                                    return (
                                                        <>
                                                            <br/>
                                                            <span>
                                                            {modifier.name} x{modifier.amount}
                                                        </span>

                                                        </>
                                                    )
                                                })}
                                    </span>
                                            : null

                                        }
                                    </div>
                                    <span>x {item.amount}</span></div>
                            </div>
                        )
                    })
                }
                <br/>
                <span
                    className="font-weight-bold">{Math.ceil(props.order.totalPrice / 100)} {CURRENCY[props.order.currency]} </span>
            </td>
            <td data-title={i18n.delivery_type()}>
                {props.order.delivery.type === "yandex" ? <YandexDeliverySummary order={props.order}/> : props.order.iiko?.order.tableNumbers?.length ?
                    i18n.in_doors()
                    :
                    <>
                        {i18n.delivery()}
                        <div className={'text-secondary'}>{props.order.iiko?.order.address}</div>
                    </>
                }
                {props.order.delivery.data?.deliveryComment && props.order.delivery.data?.deliveryComment.length > 0 ?
                    <>
                        <div className="tx-gray">{i18n.delivery_comment()}:</div>
                        <div>{props.order.delivery.data?.deliveryComment}</div>
                    </>
                    : null}
                {props.order.customerComment && props.order.customerComment.length > 0 ?
                    <>
                        <div className="tx-gray">{i18n.customerComment()}:</div>
                        <div>{props.order?.customerComment}</div>
                    </>
                    : null}
            </td>

            <td data-title={i18n.url_parameters()} style={{maxWidth: "400px"}}>

                {Object.keys(props.order.originURLParams || {}).map((key) => {
                    return (
                        <div style={{width: "100%", wordBreak: "break-all",}}>
                            <span style={{color: "orange"}}>{key}</span>
                            <span>{`=${props.order.originURLParams?.[key]}`}</span>
                        </div>
                    )
                })}

            </td>

            <td data-title={i18n.date()}>
                {props.createdAt ?
                    <span
                        style={{paddingLeft: "10px"}}>{moment(props.createdAt).format("DD.MM.YYYY HH:mm")}</span>
                    : null}
            </td>

            {user?.org?.availableModules?.includes("payments") ?
                <td data-title={i18n.status()}>
                    <OrderPaymentStatus payment={props.order.payment} i18n={i18n}/>

                </td>
                : null}

            {user?.org?.availableModules?.includes("iiko") ?
                <td data-title={i18n.iiko_creation_status()}>
                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", flexDirection: "column", height: "100%"}}>
                        {(props.iikoStatus === "DONE") && (
                            <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
                                <span className={'tags__payment'} style={{backgroundColor: "#1b9956"}}>
                                    {i18n.iiko_creation_status_success()}
                                </span>
                                {(
                                    props.iikoDetailedStatus === "Cancelled" && (
                                        <span className={'tags__payment'}
                                              style={{backgroundColor: "#ff4136"}}>
                                    {i18n.iiko_creation_status_cancelled()}
                                </span>
                                    )
                                ) || (
                                    props.iikoDetailedStatus === "Deleted" && (
                                        <span className={'tags__payment'} style={{backgroundColor: "#ff4136"}}>
                                    {i18n.iiko_creation_status_deleted()}
                                </span>
                                    )
                                ) || (
                                    (!stillCanceling && props.iikoDetailedStatus === "ReadyForCooking") && (
                                        <span className={'tags__payment'} style={{backgroundColor: "rgb(26, 122, 255)"}}>
                                    {i18n.iiko_creation_status_is_ready_for_cooking()}
                                </span>
                                    )
                                )
                                }
                                {(stillCanceling && props.iikoDetailedStatus !== "Cancelled") && (
                                    <span className={'tags__payment'} style={{backgroundColor: "#686968"}}>
                                        {i18n.iiko_order_is_in_the_process_of_cancel()}
                                    </span>
                                )}
                            </div>
                        )}
                        {props.iikoStatus === "ERROR" && <span
                            className={'tags__payment'}
                            style={{backgroundColor: "#ff4136"}}>{i18n.iiko_creation_status_error()}
                        </span>}
                        {(props.iikoStatus === "IN_PROGRESS" || (!props.iikoStatus && !isEmpty(props.order?.iikoOrderTask))) &&
                            <span
                                className={'tags__payment'}
                                style={{backgroundColor: "#f2cc56"}}>{i18n.iiko_creation_status_in_progress()}
                        </span>}
                        {user?.isStaff && props.iikoStatus === "ERROR" && (props.order.iikoErrorInfo || props.order.iikoOrderTask?.error) && <span
                            className={'tags__payment'}
                            style={{backgroundColor: "#ff4136", marginTop: "10px", overflowWrap: "anywhere"}}>
                                {props.order.iikoErrorInfo
                                    ? `${props.order.iikoErrorInfo.code ?? ""} ${props.order.iikoErrorInfo.message ?? ""}`
                                    : props.order.iikoOrderTask?.error}
                        </span>}
                    </div>
                </td>
                : null}

            {user?.org?.availableModules?.includes("rkeeper") ?
                <td data-title={i18n.rkeeper_creation_status()}/>
                : null}

            <td data-title={i18n.actions()}>
                <div className="default-table_actions">
                    <div style={{width: "100%", display: "flex", flexDirection: "column", gap: "10px"}}>

                        {(
                            iikoMenuSettings?.mustApproveOrdersBeforeSend &&
                            !props.iikoStatus &&
                            isEmpty(props.iikoOrderTask)
                        ) && (
                            <Button
                                style={{width: "100%"}}
                                type={"primary"}
                                onClick={handleApproveIikoOrder}
                                loading={loaders.approveIikoOrder}
                                disabled={loaders.approveIikoOrder}
                            >
                                {i18n.approve()}
                            </Button>
                        )}

                        {(
                            props.iikoStatus === "ERROR"
                        ) && (
                            <Button
                                style={{width: "100%"}}
                                onClick={handleRecoverIikoOrder}
                                loading={loaders.recoverIikoOrder}
                                disabled={loaders.recoverIikoOrder}
                            >
                                {i18n.iiko_recover_order()}
                            </Button>
                        )}

                        {(
                            props.iikoStatus === "DONE" &&
                            !!props.iikoOrderTask?.deliveryOrderIikoId &&
                            props.iikoDetailedStatus !== "Closed" &&
                            props.iikoDetailedStatus !== "Cancelled" &&
                            props.iikoDetailedStatus !== "Deleted" &&
                            !stillCanceling
                        ) && (
                            <Button
                                style={{width: "100%"}}
                                type={"primary"}
                                danger
                                onClick={handleCancelIikoDeliveryOrder}
                                loading={loaders.cancelIikoDeliveryOrder}
                                disabled={loaders.cancelIikoDeliveryOrder}
                            >
                                {i18n.cancel()}
                            </Button>
                        )}
                    </div>
                    <button type="button" className="default-table__button"
                            onClick={(e) => {
                                e.preventDefault()
                                addNotificationWithButton({
                                    message: i18n.q_archive_order(),
                                    onSubmit: () => onArchiveOrder(props.order.id),
                                    loading: archiveLoading,
                                    i18n
                                })
                            }}
                            title={i18n.q_archive_order()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#8392A5" viewBox="0 0 24 24">
                            <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM6.5 8h11L16 13h-3.5v2h-1v-2H8L6.5 8zM5.12 5l.81-1h12l.94 1H5.12z"/>
                        </svg>
                    </button>
                </div>
                {props.order.delivery.type === "yandex" && <YandexDeliveryControls dispatchInfo={props.order.yandexDispatch} orderId={props.order.id} number={props.order.number}/>}
            </td>

        </tr>
    )
}
